import { useForm } from "react-hook-form";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useMutation } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2Icon } from "lucide-react";
import { Input } from "./Input";
import { request } from "@/lib/request";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { PasswordInput } from "../../ui/password-input";
import { captureEvent, sanitizeErrorReason } from "@/lib/analytics";

const authenticateSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z
    .string()
    .min(12, "Password must be at least 12 characters long")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(
      /[!@#$%^&*(),.?":{}|<>\-+=_`~]/,
      "Password must contain at least one special character",
    ),
});

export type AuthenticateForm = {
  email: string;
  password: string;
};

export const AuthenticationAuthenticate = ({
  onLogin,
  goToForget,
}: {
  onLogin: (data: { access_token: string; new_user: boolean }) => void;
  goToForget: () => void;
}) => {
  const { isPending, isError, error, mutate } = useMutation({
    mutationKey: ["login/register"],
    mutationFn: async ({ email, password }: AuthenticateForm) => {
      const res = await request
        .post(`/auth/authenticate`, {
          email,
          password,
        })
        .catch(err => {
          throw new Error(
            err.response?.data.message
              ? err.response?.data.message
              : "Something went wrong, please try again.",
          );
        });

      if (res.headers["set-cookie"]) {
        const setCookie = res.headers["set-cookie"];
        if (Array.isArray(setCookie)) {
          setCookie.forEach(cookieStr => {
            document.cookie = cookieStr;
          });
        } else {
          document.cookie = setCookie;
        }
      }
      return res.data.data;
    },
    onSuccess: res => {
      captureEvent("onboarding_auth_succeeded", {
        method: "email_password",
        newUser: res.new_user,
      });
      onLogin(res);
    },
    onError: err => {
      captureEvent("onboarding_auth_failed", {
        method: "email_password",
        reason: sanitizeErrorReason(err),
      });
    },
  });

  const form = useForm<AuthenticateForm>({
    resolver: zodResolver(authenticateSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleSubmit = (values: { email: string; password: string }) => {
    captureEvent("onboarding_auth_submitted", {
      method: "email_password",
    });
    mutate({ ...values });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-grow flex-col justify-between"
      >
        <div className="flex flex-grow flex-col gap-5">
          <FormField
            control={form.control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  placeholder="Enter your email..."
                  {...field}
                />
                {error && <FormMessage>{error.message}</FormMessage>}
              </FormItem>
            )}
          />
          <FormField
            name="password"
            control={form.control}
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <PasswordInput
                  value={field.value}
                  onValueChange={field.onChange}
                />
                {error && <FormMessage>{error.message}</FormMessage>}
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-grow flex-col justify-end gap-4">
          {isError ? (
            <Alert variant="destructive" className="-mb-8 border-none p-0">
              <AlertDescription>
                {error?.message || "An error occurred, please try again."}
              </AlertDescription>
            </Alert>
          ) : null}
          <Button
            type="submit"
            variant="default"
            className="mt-6 h-auto rounded-xl py-3"
          >
            {isPending ? (
              <>
                <Loader2Icon className="mr-2 h-5 w-5 animate-spin" />
                Loading...
              </>
            ) : (
              "Login"
            )}
          </Button>
          <Button
            type="button"
            variant="link"
            className="-mt-2"
            onClick={goToForget}
          >
            Forgot your password?
          </Button>
        </div>
      </form>
    </Form>
  );
};
